import { useState } from 'react';

import { AppIcon, InfoModal } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import majorsGuideBanner from '@backrs_ui/grow-ui/src/assets/images/majors/majors-guide-banner.png';
import {
  GuidePageShell,
  GuideSections,
  GuideToolsSection,
} from '@backrs_ui/grow-ui/src/components/GuidePage';
import { LubavButton } from '@backrs_ui/grow-ui/src/components/Lubav/LubavButton';
import { StickerCardId } from '@backrs_ui/grow-ui/src/pages/Home/hooks/stickerCardMapping';
import { useGetMajorsGuideOverviewQuery } from '@backrs_ui/grow-ui/src/redux/majorsGuide/majorsGuide.api';

import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  SxProps,
  Theme,
  useMediaQuery,
} from '@mui/material';

import { FAQAccordionSection, Majors101InlineSection, MajorsByCollege } from './components';
import { useAgentContext } from './MajorsGuidePageContainer.agentContext';
import { majorsGuideContent } from '../content';
import type { DataSource, SalaryRow } from '../content/types';

type ActiveDialog = 'dataSources' | 'salaryFacts' | null;

const SECTION_IDS = {
  overview: 'majors-guide-section-overview',
  tools: 'majors-guide-section-tools',
  paths: 'majors-guide-section-paths',
  college: 'majors-guide-section-college',
  faq: 'majors-guide-section-faq',
} as const;

const MAJORS_GUIDE_TOOL_ID = 'majors_and_courses';

const MAJOR_TOOL_IDS: readonly StickerCardId[] = [
  'majors_to_careers',
  'explore_majors',
  'careers_to_majors',
  'majors',
];

export const MajorsGuidePageContainer = () => {
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  useAgentContext();

  const [activeDialog, setActiveDialog] = useState<ActiveDialog>(null);
  const [expandedCollege, setExpandedCollege] = useState<string | null>(null);

  const { data: overviewData, isLoading: isLoadingMajors } =
    useGetMajorsGuideOverviewQuery(undefined);
  const collegeTypes = overviewData?.collegeTypes ?? [];

  const sections = [
    {
      id: SECTION_IDS.tools,
      title: appStrings.modules.majorsGuide.majorToolsSection.title,
      subtitle: appStrings.modules.majorsGuide.majorToolsSection.subtitle,
      rightSlot: (
        <LubavButton
          id="majors-guide-major-tools-lubav-btn"
          label="Help me decide"
          prompt="Start with a brief, conversational one-sentence intro acknowledging the question, then give a quick 1-2 sentence description of each tool offered in the Major Tools section as bullets so the user knows which one to start with."
          allowTools={false}
        />
      ),
      content: <GuideToolsSection toolIds={MAJOR_TOOL_IDS} />,
    },
    {
      id: SECTION_IDS.paths,
      title: majorsGuideContent.labels.majorsVsCareerPaths,
      subtitle: majorsGuideContent.labels.majorsVsCareerPathsSubtitle,
      rightSlot: (
        <LubavButton
          id="majors-guide-vs-paths-lubav-btn"
          label="Which one is right for me?"
          prompt="Based on what you know about this student, which of the four major types (Direct-to-Career, Flexible Foundation, Gateway, or Career Switching) is the best fit and why? Keep it concise and personalized."
          allowTools={false}
        />
      ),
      content: <Majors101InlineSection />,
    },
    {
      id: SECTION_IDS.college,
      title: majorsGuideContent.labels.majorsByCollege,
      subtitle: majorsGuideContent.labels.majorsByCollegeSubtitle,
      rightSlot: (
        <LubavButton
          id="majors-guide-by-college-lubav-btn"
          label="Explain this to me"
          prompt="Explain what a 'typical college' means in the context of majors — why are majors grouped by college, and what does that tell a student? Then ask the student if they'd like to dig deeper into a particular college or major."
          allowTools={false}
        />
      ),
      content: (
        <MajorsByCollege
          majors={overviewData?.majors ?? []}
          collegeTypes={collegeTypes}
          isLoading={isLoadingMajors}
          expandedCollege={expandedCollege}
          onExpandedCollegeChange={setExpandedCollege}
        />
      ),
    },
    {
      id: SECTION_IDS.faq,
      title: majorsGuideContent.labels.majorsFAQs,
      subtitle: majorsGuideContent.labels.majorsFAQsSubtitle,
      rightSlot: (
        <Button
          id="majors-faq-data-source-btn"
          variant="contained"
          size="sm"
          hierarchy="Secondary gray"
          endIcon={
            <AppIcon
              iconName="InfoCircle"
              diameter="16px"
              color="Colors/Foreground/fg-quaternary"
            />
          }
          onClick={() => setActiveDialog('dataSources')}
          sx={dataSourceBtnSx}
        >
          {majorsGuideContent.labels.dataSourcesButton}
        </Button>
      ),
      content: <FAQAccordionSection faqSections={majorsGuideContent.faqSections} />,
    },
  ];

  return (
    <>
      <GuidePageShell
        toolId={MAJORS_GUIDE_TOOL_ID}
        banner={{
          src: majorsGuideBanner,
          alt: 'Backrs Guide to College Majors',
        }}
      >
        <GuideSections
          header={majorsGuideContent.header}
          overview={{ id: SECTION_IDS.overview }}
          sections={sections}
        />
      </GuidePageShell>

      {/* Data Foundation Dialog */}
      <InfoModal
        open={activeDialog === 'dataSources'}
        onClose={() => setActiveDialog(null)}
        width="md"
        featuredIcon={{
          iconName: 'InfoCircle',
          size: 'lg',
          color: 'Colors/Foreground/fg-brand-primary',
        }}
        headerContent={
          <Box sx={dataFoundationHeaderContentSx}>
            <Typography variant="text-lg-semibold" sx={dataFoundationTitleSx}>
              {majorsGuideContent.dataSources.title}
            </Typography>
            <Typography variant="body2" sx={dataFoundationSubtitleSx}>
              {majorsGuideContent.dataSources.subtitle}
            </Typography>
          </Box>
        }
        bodyContent={
          <Box sx={dialogContentSx}>
            <Box sx={dataFoundationCardSx}>
              <Box sx={dataFoundationIconSx}>
                <AppIcon iconName="LayersThree01" diameter="20px" color="Colors/Base/White" />
              </Box>
              <Box sx={dataFoundationCardContentSx}>
                <Typography variant="text-md-semibold" sx={dataFoundationCardHeadingSx}>
                  {majorsGuideContent.dataSources.sectionTitle}
                </Typography>
                <Box component="ul" sx={dataFoundationSourcesListSx}>
                  {majorsGuideContent.dataSources.sources.map(
                    (source: DataSource, index: number) => (
                      <Box component="li" key={index}>
                        <Typography
                          variant="body2"
                          component="span"
                          sx={dataFoundationSourceTextSx}
                        >
                          <Box
                            component="a"
                            href={source.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={sourceUnderlineLinkSx}
                          >
                            {source.name}
                          </Box>
                          {' - '}
                          {source.description}
                        </Typography>
                      </Box>
                    ),
                  )}
                </Box>
              </Box>
            </Box>

            {/* AI Integration Card */}
            <Box sx={dataFoundationCardSx}>
              <Box sx={dataFoundationIconSx}>
                <AppIcon iconName="Stars01" diameter="20px" color="Colors/Base/White" />
              </Box>
              <Box sx={dataFoundationCardContentSx}>
                <Typography variant="text-md-semibold" sx={dataFoundationCardHeadingSx}>
                  {majorsGuideContent.dataSources.aiIntegrationTitle}
                </Typography>
                <Typography variant="body2" sx={dataFoundationDescriptionSx}>
                  {majorsGuideContent.dataSources.aiIntegration}
                </Typography>
                <Typography variant="body2" sx={commitmentItalicSx}>
                  <Box component="span" sx={{ fontWeight: 700 }}>
                    {majorsGuideContent.labels.ourCommitment}
                  </Box>{' '}
                  {majorsGuideContent.dataSources.commitment}
                </Typography>
              </Box>
            </Box>
          </Box>
        }
      />

      {/* Salary Facts Dialog */}
      <InfoModal
        open={activeDialog === 'salaryFacts'}
        onClose={() => setActiveDialog(null)}
        width="md"
        headerContent={
          <Typography variant="h5">{majorsGuideContent.salaryDialog.title}</Typography>
        }
        bodyContent={
          <Box sx={dialogContentSx}>
            <Typography variant="body1" sx={dialogSubtitleSx}>
              {majorsGuideContent.salaryDialog.subtitle}
            </Typography>
            <TableContainer component={Paper} sx={tableContainerSx}>
              <Table size={isMobile ? 'small' : 'medium'}>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <Typography variant="text-sm-semibold">
                        {majorsGuideContent.salaryDialog.majorColumn}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="text-sm-semibold">
                        {majorsGuideContent.salaryDialog.salaryColumn}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {majorsGuideContent.salaryData.map((row: SalaryRow, index: number) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Typography variant="body2">{row.major}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">{row.startingSalary}</Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        }
      />
    </>
  );
};

// Styles
const dataSourceBtnSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
  whiteSpace: 'nowrap',
});

const dialogContentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xl'],
  position: 'relative',
  zIndex: 1,
});

const dialogSubtitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});

// Data Foundation Modal - Header content
const dataFoundationHeaderContentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xxs'],
});

const dataFoundationTitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const dataFoundationSubtitleSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

// Data Foundation Modal - Card container
const dataFoundationCardSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  gap: backrs.spacing['spacing-lg'],
  alignItems: 'flex-start',
  p: backrs.spacing['spacing-xl'],
  border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  borderRadius: backrs.radius['radius-xl'],
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
});

// Blue circular icon container
const dataFoundationIconSx: SxProps<Theme> = ({ backrs }) => ({
  width: '40px',
  height: '40px',
  borderRadius: backrs.radius['radius-full'],
  backgroundColor: backrs.colors['Colors/Foreground/fg-brand-secondary'],
  border: `1px solid ${backrs.colors['Colors/Base/Black']}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
});

// Card content container
const dataFoundationCardContentSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xxs'],
  flex: 1,
});

// Card heading
const dataFoundationCardHeadingSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
  fontWeight: 700,
});

// Sources list styling
const dataFoundationSourcesListSx: SxProps<Theme> = ({ backrs }) => ({
  listStyle: 'disc',
  pl: backrs.spacing['spacing-xl'],
  m: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
});

// Source text styling
const dataFoundationSourceTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

// Source link with underline
const sourceUnderlineLinkSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
  textDecoration: 'underline',
  '&:hover': {
    color: backrs.colors['Colors/Text/text-brand-primary'],
  },
});

// Description text
const dataFoundationDescriptionSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
});

// Commitment italic text
const commitmentItalicSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
  fontStyle: 'italic',
});

const tableContainerSx: SxProps<Theme> = ({ backrs }) => ({
  border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  borderRadius: backrs.radius['radius-md'],
});
